import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Search, X, MoreHorizontal } from 'lucide-react';
import { useHistory, useLocation } from 'react-router-dom';
import { Routes } from '../../routes';
import TradeInPricingTabs from '../../components/TradeInPricingTabs';
import AlertNotification from '../../components/AlertNotification';
import Pagination from '../../components/Pagination';
import CampaignInfoDrawer from '../../components/CampaignInfoDrawer';
import FilterDrawer from '../../components/FilterDrawer';
import noBrandImage from '../../assets/img/brand/no-brand-img.png';
import emptySearchImg from '../../assets/img/brand/empty-search.png';
import apiHelper from '../../apiHelper';
import DiscardConfirmationModal from '../../components/DiscardConfirmationModal';

// ─── Local sort header (fixes icon overlap without touching shared component) ──
const ChevronUp = ({ color }) => (
    <svg width={12} height={12} viewBox="0 0 24 24" fill={color} xmlns="http://www.w3.org/2000/svg">
        <path d="M12 8l6 6H6l6-6z" />
    </svg>
);
const ChevronDown = ({ color }) => (
    <svg width={12} height={12} viewBox="0 0 24 24" fill={color} xmlns="http://www.w3.org/2000/svg" style={{marginTop:'-12px'}}>
        <path d="M12 16l-6-6h12l-6 6z"  />
    </svg>
);
const PromoSortHeader = ({ label, field, sortField, sortOrder, handleSort, style = {}, resizeHandle = null }) => (
    <th style={{ cursor: 'pointer', ...style }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontFamily: 'Montserrat', fontSize: 12, fontWeight: 700, color: '#212529' }}>
            {label}
            <span onClick={() => handleSort(field)} style={{ display: 'flex', flexDirection: 'column', lineHeight: 0 }}>
                <ChevronUp  color={sortField === field && sortOrder === 'asc'  ? '#212529' : '#6C757D'} />
                <ChevronDown color={sortField === field && sortOrder === 'desc' ? '#212529' : '#6C757D'}/>
            </span>
        </span>
        {resizeHandle}
    </th>
);

const PAGE_SIZE = 20;

// ─── Normalize API status → unified label ────────────────────────────────────
// API returns distinct strings: Upcoming, Ongoing, Cancelled, Ended-Early, Ended-On schedule
const normalizeStatus = (status) => (status || '').trim() || 'Ended-Early';

// kept for StatusBadge (receives already-normalized string, passes through unchanged)
const getStatusLabel = (status) => status || '';

// ─── Status Badge ─────────────────────────────────────────────────────────────
// Keys must match the strings returned by normalizeStatus above.
const STATUS_CONFIG = {
    Upcoming:             { bg: '#F8F9FA', color: '#6C757D', border: '1px solid #6C757D', noteColor: '#6C757D', label: 'Upcoming' },
    Ongoing:              { bg: '#D1E7DD', color: '#198754', border: '1px solid #198754', noteColor: '#6C757D', label: 'Ongoing' },
    Cancelled:              { bg: '#F8D7DA', color: '#DC3545', border: '1px solid #DC3545', noteColor: '#6C757D', label: 'Cancelled' },
    'Ended-Early':          { bg: '#E9ECEF', color: '#212529', border: '1px solid #212529', noteColor: '#6C757D', label: 'Ended-Early' },
    'Ended-On schedule':    { bg: '#E9ECEF', color: '#212529', border: '1px solid #212529', noteColor: '#6C757D', label: 'Ended-On schedule' },
};

const StatusBadge = ({ status, note }) => {
    const label = getStatusLabel(status);
    const cfg = STATUS_CONFIG[label] || STATUS_CONFIG['Ended-Early'];
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                fontFamily: 'Montserrat', background: cfg.bg, color: cfg.color,
                border: cfg.border, whiteSpace: 'nowrap', width: 'fit-content',
            }}>
                {cfg.label || label}
            </span>
            {note && (
                <div style={{  color: cfg.noteColor,display:'flex',alignItems:'center',marginTop:'8px',gap:3 }}>
                    <div style={{height:'6px',width:'6px',borderRadius:'50%',background:cfg.color }}>

                    </div>
                    <div style={{fontSize: 11, fontFamily: 'Montserrat',}}>{note}</div> 
                </div>
            )}
        </div>
    );
};

// ─── Filter Icons ─────────────────────────────────────────────────────────────
const FilterActiveIcon = () => (
    <svg style={{ margin: 0 }} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M20.6325 6.44444C20.7252 6.24267 20.7746 6.02858 20.8107 5.74756C21.0354 4.00089 21.1478 3.12756 21.6356 2.56378C20.1234 2 19.2177 2 17.4063 2H4.59369C2.78227 2 1.87655 2 1.36439 2.56378C0.852232 3.12756 0.964572 4.00089 1.18925 5.74756C1.25487 6.25766 1.36399 6.54725 1.69704 6.94296C2.77293 8.22128 4.74334 10.4951 7.50829 12.5623C7.76095 12.7512 7.92676 13.0599 7.95475 13.4016C8.26623 17.2071 8.55159 19.8068 8.70309 21.0865C8.78256 21.7577 9.52673 22.274 10.14 21.8404C11.1702 21.1118 13.0947 20.2952 13.349 18.938C13.4692 18.2964 13.6451 17.1765 13.8437 15.3333" stroke="#0D6EFD" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <g clipPath="url(#clip0_promo_filter)">
            <path d="M22.8125 11C22.8125 14.2102 20.2102 16.8125 17 16.8125C13.7898 16.8125 11.1875 14.2102 11.1875 11C11.1875 7.78987 13.7898 5.18753 17 5.18753C20.2102 5.18753 22.8125 7.78987 22.8125 11ZM16.3277 14.0777L20.6402 9.7652C20.7866 9.61877 20.7866 9.38132 20.6402 9.23488L20.1099 8.70456C19.9634 9.5581 19.726 9.5581 19.5795 8.70456L16.0625 12.2215L14.4205 10.5795C14.2741 10.4331 14.0366 10.4331 13.8901 10.5795L13.3598 11.1099C13.2134 11.2563 13.2134 11.4937 13.3598 11.6402L15.7973 14.0777C15.9438 14.2241 16.1812 14.2241 16.3277 14.0777Z" fill="#0D6EFD" />
        </g>
        <defs>
            <clipPath id="clip0_promo_filter">
                <rect width="12" height="12" fill="white" transform="translate(11 5)" />
            </clipPath>
        </defs>
    </svg>
);

const FilterInactiveIcon = ({ color = '#6C757D' }) => (
    <svg style={{ margin: 0 }} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path d="M21.6323 6.44444C21.7251 6.24267 21.7744 6.02858 21.8106 5.74756C22.0353 4.00089 22.1476 3.12756 21.6354 2.56378C21.1233 2 20.2176 2 18.4062 2H5.59366C3.78225 2 2.87655 2 2.36439 2.56378C1.85223 3.12756 1.96457 4.00089 2.18925 5.74756C2.25487 6.25766 2.36399 6.54725 2.69703 6.94296C3.77291 8.22128 5.74331 10.4951 8.50823 12.5623C8.76089 12.7512 8.9267 13.0599 8.95469 13.4016C9.26616 17.2071 9.55152 19.8068 9.70302 21.0865C9.78249 21.7577 10.5267 22.274 11.1399 21.8404C12.1701 21.1118 14.0946 20.2952 14.3489 18.938C14.4691 18.2964 14.645 17.1765 14.8436 15.3333" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M18.1113 7.5556V15.3334M22.0001 11.4445L14.2224 11.4445" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

// ─── Component ────────────────────────────────────────────────────────────────
export default function PromotionalCampaign() {
    const history = useHistory();
    const location = useLocation();
    const [campaigns, setCampaigns] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    // ── Success toast after save (passed via navigation state) ──
    const [notification, setNotification] = useState({ show: false, message: '' });

    const [colWidths, setColWidths] = useState({
        campaignName: 200,
        // Longest chip is "Ended-On schedule", measured at 134.5px including the
        // pill's own padding; +20px cell padding puts the clipping point at 155px,
        // which is the resize floor. The default leaves a little room past that.
        status: 175,
        itemQty: 100,
        promoPeriod: 239,
        lastModified: 160,
    });

    // Columns the user has dragged. Once dragged, a column stops absorbing
    // leftover space so a drag moves only the column being dragged.
    const [resizedCols, setResizedCols] = useState({});

    const tableWrapperRef = useRef(null);
    const [containerWidth, setContainerWidth] = useState(0);

    useEffect(() => {
        const measure = () => setContainerWidth(tableWrapperRef.current?.clientWidth || 0);
        measure();
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, []);

    const ACTION_COL_WIDTH = 60;
    const COL_KEYS = ['campaignName', 'status', 'itemQty', 'promoPeriod', 'lastModified'];

    // Mirrors DynamicTable: an untouched column takes an even share of the
    // leftover space so the table still fills the wrapper, but a column that has
    // been dragged keeps exactly its own width.
    const getDisplayColWidth = (key) => {
        const base = colWidths[key];
        if (resizedCols[key] || !containerWidth) return base;
        const stretchable = COL_KEYS.filter((k) => !resizedCols[k]);
        if (!stretchable.length) return base;
        const baseTotal = ACTION_COL_WIDTH + COL_KEYS.reduce((sum, k) => sum + colWidths[k], 0);
        const leftover = containerWidth - baseTotal;
        if (leftover <= 0) return base;
        return base + leftover / stretchable.length;
    };

    // An explicit pixel total, not 100%. At 100% the table is pinned to the
    // wrapper, so widening a column can only come out of its neighbours and
    // narrowing one just hands the space straight back to them — which is why
    // dragging looked like it did nothing in this table.
    const getTableWidth = () =>
        ACTION_COL_WIDTH + COL_KEYS.reduce((sum, k) => sum + getDisplayColWidth(k), 0);

    // Slack between the summed columns and the wrapper. Once a column has been
    // dragged nothing stretches any more, so narrowing one leaves the sum short
    // of the container and the table pulls away from its right edge. Rendered as
    // an inert filler cell so the table stays full width without handing the
    // freed space back to the other columns.
    const getFillerWidth = () => Math.max(0, containerWidth - getTableWidth());

    const handleColResizeMouseDown = (e, key, startWidth, minWidth = 90) => {
        e.preventDefault();
        e.stopPropagation();
        // Freeze every column at its rendered width before the drag starts, so
        // the stretch share can't shift underneath and move columns the user
        // isn't touching.
        const frozen = {};
        COL_KEYS.forEach((k) => { frozen[k] = getDisplayColWidth(k); });
        setColWidths(frozen);
        setResizedCols(Object.fromEntries(COL_KEYS.map((k) => [k, true])));

        const startX = e.clientX;
        const dragStart = frozen[key];
        const onMouseMove = (moveEvent) => {
            const delta = moveEvent.clientX - startX;
            setColWidths((prev) => ({ ...prev, [key]: Math.max(minWidth, dragStart + delta) }));
        };
        const onMouseUp = () => {
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        };
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
    };

    const ColResizeHandle = ({ colKey, minWidth }) => (
        <div
            className="col-resize-handle"
            onMouseDown={(e) => handleColResizeMouseDown(e, colKey, colWidths[colKey], minWidth)}
            onClick={(e) => e.stopPropagation()}
            style={{ position: 'absolute', right: 0, top: 0, height: '100%', width: '6px', cursor: 'col-resize', zIndex: 2, userSelect: 'none' }}
        />
    );

    useEffect(() => {
        if (location.state?.successMessage) {
            setNotification({ show: true, message: location.state.successMessage });
            window.history.replaceState({}, document.title);
        }
    }, []); // eslint-disable-line

    useEffect(() => {
        setIsLoading(true);
        apiHelper('sales/promotional-campaigns/', 'GET')
            .then(res => res.ok ? res.json() : [])
            .then(data => {
                const rawRows = Array.isArray(data) ? data : (data.results ?? []);
                const rows = rawRows.map(row => ({
                    ...row,
                    status: normalizeStatus(row.status),
                }));
                setCampaigns(rows);
            })
            .catch(() => setCampaigns([]))
            .finally(() => setIsLoading(false));
    }, []);

    // ── Search ──
    const [searchInput, setSearchInput] = useState('');
    const [searchTerm,  setSearchTerm]  = useState('');

    // ── Filter ──
    const [appliedFilter,    setAppliedFilter]    = useState(null);
    const [filterPopupOpen,  setFilterPopupOpen]  = useState(false);
    const [filterTempValues, setFilterTempValues] = useState({
        type: [],
        changesTime_start: null,
        changesTime_end: null,
    });

    const STATUS_TYPE_OPTIONS = [
        { id: 'Upcoming',                  title: 'Upcoming' },
        { id: 'Ongoing',                   title: 'Ongoing' },
        { id: 'Ended-Early',        title: 'Ended-Early' },
        { id: 'Ended-On schedule',  title: 'Ended-On schedule' },
        { id: 'Cancelled',          title: 'Cancelled' },
    ];

    const promoFilterSections = [
        {
            label: 'Status',
            type: 'list',
            key: 'type',
            data: STATUS_TYPE_OPTIONS,
            renderItem: (item) => ({ id: item.id, title: item.title }),
        },
        // {
        //     label: 'Promotional Period',
        //     type: 'dateRangeWithTime',
        //     key: 'changesTime',
        // },
    ];

    // ── Sort ──
    const [sortField, setSortField] = useState('lastModified');
    const [sortOrder, setSortOrder] = useState('desc');

    // ── Pagination ──
    const [currentPage, setCurrentPage] = useState(1);

    // ── Drawer ──
    const [drawerRow, setDrawerRow] = useState(null);

    // ── Action Dropdown ──
    const [dropdownOpenId,   setDropdownOpenId]   = useState(null);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0 });
    const dropdownRef = useRef(null);
    const buttonRefs  = useRef({});

    // ── Confirmation Modals ──
    const [cancelConfirmRow, setCancelConfirmRow] = useState(null);
    const [endConfirmRow,    setEndConfirmRow]    = useState(null);

    const isInitialTableEmpty = !isLoading && campaigns.length === 0;
    const isSearching = searchTerm?.trim().length > 0;
    const isFilterApplied = !!appliedFilter;
    const isTableEmpty = isInitialTableEmpty && !isSearching && !isFilterApplied;

    // ── SGT-formatted "now" for optimistic end-time display ──
    const nowSGT = () => {
        const parts = new Intl.DateTimeFormat('en-CA', {
            timeZone: 'Asia/Singapore',
            year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit', hour12: false,
        }).formatToParts(new Date());
        const p = Object.fromEntries(parts.map(({ type, value }) => [type, value]));
        return `${p.year}-${p.month}-${p.day} ${p.hour}:${p.minute}`;
    };

    // ── End campaign early ──
    const handleEndCampaign = async (campaignId) => {
        const prevCampaigns = campaigns;
        const prevDrawerRow = drawerRow;
        const endedAt = nowSGT();

        // Optimistic update
        setCampaigns(prev => prev.map(c =>
            c.id === campaignId
                ? { ...c, status: 'Ended-Early', promoEnd: endedAt }
                : c
        ));
        setDrawerRow(prev => prev?.id === campaignId ? { ...prev, status: 'Ended-Early', promoEnd: endedAt } : prev);

        try {
            const res = await apiHelper(`sales/promotional-campaigns/${campaignId}/end/`, 'PATCH');
            if (res && res.ok) {
                const data = await res.json();
                console.log('[WCM End Campaign] campaign_id:', campaignId);
                console.log('[WCM End Campaign] deleted_uuids:', data.wcm_sync?.deleted_uuids);
                console.log('[WCM End Campaign] delete_result:', data.wcm_sync?.delete_result);
                if (data.wcm_sync?.restore) {
                    data.wcm_sync.restore.forEach((r, i) => {
                        console.log(`[WCM End Campaign] restore[${i}] listing_id=${r.listing_id} payload_sent:`, r.payload_sent);
                        console.log(`[WCM End Campaign] restore[${i}] wcm_result:`, r.wcm_result);
                    });
                }
                const updated = { ...data, status: normalizeStatus(data.status) };
                setCampaigns(prev => prev.map(c => c.id === campaignId ? { ...c, ...updated } : c));
                setDrawerRow(prev => prev?.id === campaignId ? { ...prev, ...updated } : prev);
            } else {
                setCampaigns(prevCampaigns);
                setDrawerRow(prevDrawerRow);
            }
        } catch {
            setCampaigns(prevCampaigns);
            setDrawerRow(prevDrawerRow);
        }
    };

    // ── Cancel campaign ──
    const handleCancelCampaign = async (campaignId) => {
        const prevCampaigns = campaigns;
        const prevDrawerRow = drawerRow;

        // Optimistic update
        setCampaigns(prev => prev.map(c =>
            c.id === campaignId
                ? { ...c, status: 'Cancelled', promoStart: null, promoEnd: null }
                : c
        ));
        if (drawerRow?.id === campaignId) {
            setDrawerRow(prev => ({ ...prev, status: 'Cancelled', promoStart: null, promoEnd: null }));
        }

        try {
            const res = await apiHelper(`sales/promotional-campaigns/${campaignId}/cancel/`, 'PATCH');
            if (res && res.ok) {
                const data = await res.json();
                console.log('[WCM Cancel Campaign] campaign_id:', campaignId, '— no WCM sync (cancel requires no WCM action)');
                const updated = { ...data, status: normalizeStatus(data.status) };
                setCampaigns(prev => prev.map(c => c.id === campaignId ? { ...c, ...updated } : c));
                setDrawerRow(prev => prev?.id === campaignId ? { ...prev, ...updated } : prev);
            } else {
                setCampaigns(prevCampaigns);
                setDrawerRow(prevDrawerRow);
            }
        } catch {
            setCampaigns(prevCampaigns);
            setDrawerRow(prevDrawerRow);
        }
    };

    // ── Sort handler ──
    const handleSort = (field) => {
        if (sortField === field) {
            setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortOrder('asc');
        }
        setCurrentPage(1);
    };

    // ── Filtered + Sorted data ──
    const filtered = useMemo(() => {
        let data = [...campaigns];

        if (searchTerm) {
            const lower = searchTerm.toLowerCase();
            data = data.filter(r => r.name.toLowerCase().includes(lower));
        }

        if (appliedFilter?.type?.length) {
            data = data.filter(r => appliedFilter.type.includes(r.status));
        }

        if (appliedFilter?.changesTime_start || appliedFilter?.changesTime_end) {
            const filterStart = appliedFilter.changesTime_start ? new Date(appliedFilter.changesTime_start) : null;
            const filterEnd   = appliedFilter.changesTime_end   ? new Date(appliedFilter.changesTime_end)   : null;
            data = data.filter(r => {
                // Include campaigns that overlap the filter range at all (partial overlap counts)
                const campaignStart = new Date(r.promoStart);
                const campaignEnd   = new Date(r.promoEnd);
                if (filterStart && campaignEnd   < filterStart) return false; // campaign ended before filter range starts
                if (filterEnd   && campaignStart > filterEnd)   return false; // campaign starts after filter range ends
                return true;
            });
        }

        if (sortField) {
            data.sort((a, b) => {
                if (sortField === 'lastModified' || sortField === 'promoStart') {
                    const aDate = new Date(a[sortField] ?? 0);
                    const bDate = new Date(b[sortField] ?? 0);
                    return sortOrder === 'asc' ? aDate - bDate : bDate - aDate;
                }
                const aVal = a[sortField] ?? '';
                const bVal = b[sortField] ?? '';
                if (typeof aVal === 'string' || typeof bVal === 'string') {
                    const cmp = String(aVal).localeCompare(String(bVal), undefined, { sensitivity: 'base', numeric: true });
                    return sortOrder === 'asc' ? cmp : -cmp;
                }
                if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
                if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
                return 0;
            });
        }

        return data;
    }, [campaigns, searchTerm, appliedFilter, sortField, sortOrder]);

    const totalCount = filtered.length;
    const totalPages = Math.ceil(totalCount / PAGE_SIZE);
    const pageData   = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
    const rangeStart = totalCount === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
    const rangeEnd   = Math.min(currentPage * PAGE_SIZE, totalCount);

    // ── Dropdown open/close ──
    const toggleDropdown = (id, e) => {
        e.stopPropagation();
        if (dropdownOpenId === id) { setDropdownOpenId(null); return; }
        const btn = buttonRefs.current[id];
        if (btn) {
            const rect = btn.getBoundingClientRect();
            setDropdownPosition({ top: rect.bottom + window.scrollY + 4, right: window.innerWidth - rect.right });
        }
        setDropdownOpenId(id);
    };

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setDropdownOpenId(null);
        };
        if (dropdownOpenId !== null) document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [dropdownOpenId]);

    useEffect(() => {
        const handleScroll = () => setDropdownOpenId(null);
        window.addEventListener('scroll', handleScroll, true);
        return () => window.removeEventListener('scroll', handleScroll, true);
    }, []);

    // ── Cron polling — runs every 5 min while page is open ────────────────────
    useEffect(() => {
        const runCron = () => {
            console.log('[Cron] Checking promotional campaigns...');
            apiHelper('sales/promotional-campaigns/cron/', 'POST')
                .then(res => res.ok ? res.json() : Promise.reject(res.status))
                .then(data => {
                    console.log('[Cron] Result:', data);
                    if (data.started?.length) {
                        data.started.forEach(c => {
                            console.log(`[Cron] STARTED campaign "${c.name}" (id=${c.id})`);
                            console.log(`[Cron]   WCM delete normal prices:`, c.wcm?.delete_normal);
                            console.log(`[Cron]   WCM push promo prices:`,    c.wcm?.push_promo);
                        });
                    }
                    if (data.ended?.length) {
                        data.ended.forEach(c => {
                            console.log(`[Cron] ENDED campaign "${c.name}" (id=${c.id})`);
                            console.log(`[Cron]   WCM delete promo prices:`, c.wcm?.delete_promo);
                            (c.wcm?.restore || []).forEach((r, i) => {
                                console.log(`[Cron]   WCM restore[${i}] listing_id=${r.listing_id}:`, r.wcm_result);
                            });
                        });
                    }
                    if (!data.started?.length && !data.ended?.length) {
                        console.log('[Cron] No campaigns to activate or end.');
                    }
                    // Refresh list if any status changed
                    if (data.started?.length || data.ended?.length) {
                        apiHelper('sales/promotional-campaigns/', 'GET')
                            .then(res => res.ok ? res.json() : null)
                            .then(d => {
                                if (!d) return;
                                const rows = (Array.isArray(d) ? d : (d.results ?? [])).map(row => ({
                                    ...row, status: normalizeStatus(row.status),
                                }));
                                setCampaigns(rows);
                            })
                            .catch(() => {});
                    }
                })
                .catch(err => console.warn('[Cron] Error calling cron endpoint:', err));
        };

        const interval = setInterval(runCron, 60 * 1000); // every 1 minute
        return () => clearInterval(interval);
    }, []);

    const currentRow = campaigns.find(r => r.id === dropdownOpenId);

    // ─── Render ───────────────────────────────────────────────────────────────
    return (
        <div className="company-body new-look stock-wrap bg-checkbox">
            <TradeInPricingTabs />

            <div className="employee-tabs-container">
                {notification.show && (
                    <AlertNotification
                        type="success"
                        recordName=""
                        action="create"
                        message={notification.message}
                        onClose={() => setNotification({ show: false, message: '' })}
                    />
                )}
                <div className="tab-content">
                    <div className="info-section">
                        <div className="info-card" style={{ overflow: 'unset' }}>
                            <div className="entities-tab">

                                {/* ── Toolbar ── */}
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <div style={{ display: 'flex', gap: '8px' }}>

                                        {/* Search Box */}
                                        <div
                                            className="search-box"
                                            style={{
                                                width: '300px',
                                                position: 'relative',
                                                border: isTableEmpty ? '1px solid var(--Gray-400, #CED4DA)' : '1px solid var(--Gray-600, #6C757D)',
                                                background: isTableEmpty ? 'var(--Gray-100, #F8F9FA)' : 'var(--Default-White, #FFF)',
                                                pointerEvents: isTableEmpty ? 'none' : 'auto',
                                            }}
                                        >
                                            <Search size={20} color={isTableEmpty ? '#CED4DA' : '#6C757D'} />
                                            {isTableEmpty && (
                                                <style>{`.search-box-disabled-promo::placeholder { color: #CED4DA !important; }`}</style>
                                            )}
                                            <input
                                                type="text"
                                                placeholder="Search..."
                                                disabled={isTableEmpty}
                                                className={isTableEmpty ? 'search-box-disabled-promo' : undefined}
                                                style={{ color: isTableEmpty ? '#ADB5BD' : undefined }}
                                                value={searchInput}
                                                onChange={e => setSearchInput(e.target.value)}
                                                onKeyDown={e => {
                                                    if (e.key === 'Enter') { setSearchTerm(searchInput); setCurrentPage(1); }
                                                }}
                                            />
                                            {searchInput && (
                                                <button
                                                    onClick={() => { setSearchInput(''); setSearchTerm(''); setCurrentPage(1); }}
                                                    style={{
                                                        position: 'absolute', right: '4px',
                                                        background: 'transparent', border: 'none',
                                                        cursor: 'pointer', display: 'flex',
                                                        alignItems: 'center', justifyContent: 'center', padding: '4px',
                                                    }}
                                                >
                                                    <X size={16} />
                                                </button>
                                            )}
                                        </div>

                                        {/* Filter Button */}
                                        <button
                                            className={`icon-btn p-2 filter-btn ${appliedFilter ? 'applied' : ''}`}
                                            onClick={() => {
                                                if (isTableEmpty) return;
                                                setFilterPopupOpen(prev => !prev);
                                            }}
                                            disabled={isTableEmpty}
                                            style={{
                                                border: isTableEmpty
                                                    ? '1px solid var(--Gray-400, #CED4DA)'
                                                    : appliedFilter
                                                        ? '1px solid #2C7BE5'
                                                        : '1px solid var(--Gray-600, #6C757D)',
                                                color: isTableEmpty ? '#CED4DA' : appliedFilter ? '#2C7BE5' : '#6C757D',
                                                background: isTableEmpty
                                                    ? 'var(--Gray-100, #F8F9FA)'
                                                    : appliedFilter
                                                        ? '#CFE2FF'
                                                        : 'var(--Default-White, #FFF)',
                                                height: '40px',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                pointerEvents: isTableEmpty ? 'none' : 'auto',
                                                cursor: isTableEmpty ? 'not-allowed' : 'pointer',
                                                borderRadius: '8px',
                                            }}
                                        >
                                            {appliedFilter && !isTableEmpty
                                                ? <FilterActiveIcon />
                                                : <FilterInactiveIcon color={isTableEmpty ? '#CED4DA' : '#6C757D'} />
                                            }
                                        </button>

                                        {/* Filter Drawer */}
                                        <FilterDrawer
                                            isOpen={filterPopupOpen}
                                            onClose={() => setFilterPopupOpen(false)}
                                            sections={promoFilterSections}
                                            tempValues={filterTempValues}
                                            setTempValues={setFilterTempValues}
                                            onApply={() => {
                                                const hasType  = filterTempValues.type?.length > 0;
                                                const hasTime  = filterTempValues.changesTime_start || filterTempValues.changesTime_end;
                                                setAppliedFilter(
                                                    hasType || hasTime
                                                        ? {
                                                            type: filterTempValues.type,
                                                            changesTime_start: filterTempValues.changesTime_start,
                                                            changesTime_end:   filterTempValues.changesTime_end,
                                                          }
                                                        : null
                                                );
                                                setCurrentPage(1);
                                                setFilterPopupOpen(false);
                                            }}
                                            onClear={() => {
                                                setFilterTempValues({ type: [], changesTime_start: null, changesTime_end: null });
                                                setAppliedFilter(null);
                                            }}
                                        />
                                    </div>
                                </div>

                                {/* ── Table ── */}
                                <div className="table-wrapper" ref={tableWrapperRef} style={{ border: 'none' }}>
                                    {isLoading ? (
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 300, gap: 16, fontFamily: 'Montserrat' }}>
                                            <div style={{ width: 36, height: 36, border: '4px solid #f3f3f3', borderTop: '4px solid #FFEB00', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                                            <span style={{ fontSize: 14, fontWeight: 600, color: '#212529' }}>Loading campaigns...</span>
                                            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                                        </div>
                                    ) : campaigns.length === 0 ? (
                                        <div className="empty-state">
                                            <div className="empty-state-content">
                                                <div className="icon">
                                                    <img src={noBrandImage} alt="empty" style={{ width: 120, height: 120 }} />
                                                </div>
                                                <h1 className="no-item-title mt-2">No Promotional Campaign Found</h1>
                                                <p className="no-item-text">Nothing here yet. Initiate Promotional Pricing to get started!</p>
                                            </div>
                                        </div>
                                    ) : filtered.length === 0 ? (
                                        <div className="empty-state">
                                            <div className="empty-state-content">
                                                <div className="icon">
                                                    <img src={emptySearchImg} alt="no results" style={{ width: 120, height: 120 }} />
                                                </div>
                                                <h1 className="no-item-title mt-2">No Results Found</h1>
                                                <p className="no-item-text">No results match what you're looking for</p>
                                            </div>
                                        </div>
                                    ) : (
                                        <table className="entities-table" style={{ width: `${getTableWidth() + getFillerWidth()}px`, borderCollapse: 'collapse', fontFamily: 'Montserrat', fontSize: 13, tableLayout: 'fixed' }}>
                                            <thead>
                                                <tr style={{ borderBottom: '1px solid #DEE2E6' }}>
                                                    <PromoSortHeader
                                                        label="Campaign Name" field="name" sortField={sortField} sortOrder={sortOrder} handleSort={handleSort}
                                                        style={{ padding: '10px', textAlign: 'left', position: 'relative', overflow: 'hidden', width: getDisplayColWidth('campaignName') }}
                                                        resizeHandle={<ColResizeHandle colKey="campaignName" minWidth={120} />}
                                                    />
                                                    <PromoSortHeader
                                                        label="Status" field="status" sortField={sortField} sortOrder={sortOrder} handleSort={handleSort}
                                                        style={{ padding: '10px', textAlign: 'left', position: 'relative', overflow: 'hidden', width: getDisplayColWidth('status') }}
                                                        resizeHandle={<ColResizeHandle colKey="status" minWidth={155} />}
                                                    />
                                                    <PromoSortHeader
                                                        label="Item Qty" field="itemQty" sortField={sortField} sortOrder={sortOrder} handleSort={handleSort}
                                                        style={{ padding: '10px', textAlign: 'left', position: 'relative', overflow: 'hidden', width: getDisplayColWidth('itemQty') }}
                                                        resizeHandle={<ColResizeHandle colKey="itemQty" minWidth={120} />}
                                                    />
                                                    <th style={{ padding: '10px', textAlign: 'left', fontFamily: 'Montserrat', fontSize: 12, fontWeight: 700, color: '#212529', position: 'relative', overflow: 'hidden', width: getDisplayColWidth('promoPeriod') }}>
                                                        Promotional Period
                                                        <ColResizeHandle colKey="promoPeriod" minWidth={120} />
                                                    </th>
                                                    <PromoSortHeader
                                                        label="Last Modified Time" field="lastModified" sortField={sortField} sortOrder={sortOrder} handleSort={handleSort}
                                                        style={{ padding: '10px', textAlign: 'left', position: 'relative', overflow: 'hidden', width: getDisplayColWidth('lastModified') }}
                                                        resizeHandle={<ColResizeHandle colKey="lastModified" minWidth={120} />}
                                                    />
                                                    {getFillerWidth() > 0 && (
                                                        <th aria-hidden="true" style={{ padding: 0, width: getFillerWidth() }} />
                                                    )}
                                                    <th style={{ padding: '10px', textAlign: 'center', fontFamily: 'Montserrat', fontSize: 12, fontWeight: 700, color: '#212529', width: 60, minWidth: 60, maxWidth: 60 }}>Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {pageData.map((row, idx) => (
                                                    <tr
                                                        key={row.id}
                                                        onClick={() => setDrawerRow(row)}
                                                        style={{
                                                            borderBottom: '1px solid #F2F2F2',
                                                            background: idx % 2 === 0 ? '#FFF' : '#FAFAFA',
                                                            cursor: 'pointer',
                                                        }}
                                                    >
                                                        <td style={{ padding: '10px', fontWeight: 400, color: '#212529', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{row.name}</td>
                                                        <td style={{ padding: '10px', overflow: 'hidden' }}>
                                                            <StatusBadge status={row.status} />
                                                        </td>
                                                        <td style={{ padding: '10px', color: '#212529', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{row.itemQty}</td>
                                                        <td style={{ padding: '10px', color: '#212529', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                                                            {row.status === 'Cancelled' || !(row.promoStart && row.promoEnd) ? '-' : `${row.promoStart} - ${row.promoEnd}`}
                                                        </td>
                                                        <td style={{ padding: '10px', color: '#212529', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{row.lastModified}</td>
                                                        {getFillerWidth() > 0 && <td aria-hidden="true" style={{ padding: 0 }} />}
                                                        <td style={{ padding: '10px', textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                                                            <button
                                                                ref={el => buttonRefs.current[row.id] = el}
                                                                onClick={e => toggleDropdown(row.id, e)}
                                                                style={{
                                                                    background: 'transparent', border: 'none', cursor: 'pointer',
                                                                    padding: '4px 8px', borderRadius: 6, color: '#6C757D',
                                                                    display: 'inline-flex', alignItems: 'center',
                                                                }}
                                                            >
                                                                <MoreHorizontal size={18} />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                </div>

                                {/* ── Pagination ── */}
                                {filtered.length > 0 && (
                                    <div className="pagination">
                                        <div className="pagination-info">
                                            {`${rangeStart} - ${rangeEnd} of ${totalCount}`}
                                        </div>
                                        <Pagination
                                            currentPage={currentPage}
                                            totalPages={totalPages}
                                            onPageChange={setCurrentPage}
                                        />
                                    </div>
                                )}

                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* ── Action Dropdown ── */}
            {dropdownOpenId !== null && currentRow && (
                <div
                    ref={dropdownRef}
                    className="backdrop-cls"
                    style={{
                        position: 'fixed',
                        top: dropdownPosition.top,
                        right: dropdownPosition.right,
                        background: 'white',
                        boxShadow: '0px 1px 4px 0px #0000001A',
                        zIndex: 1000,
                        minWidth: 160,
                        borderRadius: 8,
                    }}
                    onClick={e => e.stopPropagation()}
                >
                    <div className="btn-align-forall">
                        <div className="common-for-all">
                            <button onClick={() => { setDrawerRow(currentRow); setDropdownOpenId(null); }}>View Details</button>
                        </div>

                        {(currentRow.status === 'Upcoming' || currentRow.status === 'Ongoing') && (
                            <div className="common-for-all">
                                <button onClick={() => { setDropdownOpenId(null); history.push(Routes.TradeInPricingEditCampaign.path, { campaign: currentRow }); }}>Edit</button>
                            </div>
                        )}

                        {currentRow.status === 'Upcoming' && (
                            <div className="common-for-all">
                                <button className="deactivate-cls" onClick={() => { setCancelConfirmRow(currentRow); setDropdownOpenId(null); }}>Cancel</button>
                            </div>
                        )}

                        {currentRow.status === 'Ongoing' && (
                            <div className="common-for-all">
                                <button className="deactivate-cls" onClick={() => { setEndConfirmRow(currentRow); setDropdownOpenId(null); }}>End</button>
                            </div>
                        )}

                        {currentRow.status === 'Cancelled' && null}
                    </div>
                </div>
            )}

            {/* ── Cancel Confirmation Modal ── */}
            <DiscardConfirmationModal
                isOpen={!!cancelConfirmRow}
                entityName="Cancel Campaign"
                onCancel={() => setCancelConfirmRow(null)}
                onConfirm={() => {
                    handleCancelCampaign(cancelConfirmRow.id);
                    setCancelConfirmRow(null);
                }}
            />

            {/* ── End Confirmation Modal ── */}
            <DiscardConfirmationModal
                isOpen={!!endConfirmRow}
                entityName="End Campaign"
                onCancel={() => setEndConfirmRow(null)}
                onConfirm={() => {
                    handleEndCampaign(endConfirmRow.id);
                    setEndConfirmRow(null);
                }}
            />

            {/* ── Campaign Info Drawer ── */}
            {drawerRow && (
                <CampaignInfoDrawer
                    row={drawerRow}
                    onClose={() => setDrawerRow(null)}
                    onEdit={(row) => { setDrawerRow(null); history.push(Routes.TradeInPricingEditCampaign.path, { campaign: row }); }}
                    onCancelCampaign={handleCancelCampaign}
                    onEndCampaign={handleEndCampaign}
                />
            )}

            <style jsx>{`
                .col-resize-handle::after {
                    content: "";
                    position: absolute;
                    top: 50%;
                    transform: translateY(-50%);
                    height: 50%;
                    left: 3px;
                    width: 2px;
                    background: #DEE2E6;
                }
                .search-box {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 8px 12px;
                    border: 1px solid #DEE2E6;
                    border-radius: 8px;
                    background: white;
                }
                .search-box input {
                    border: none;
                    outline: none;
                    width: 100%;
                    font-size: 14px;
                    background: transparent;
                }
            `}</style>
        </div>
    );
}
